import { computeSlaDeadline } from './sla.js';
import { CANONICAL_ROLES, RULE_TIERS, TIER_RANK, isCanonicalRole, isRuleTier, } from './jurisdiction-applicability-service.js';
export const JURISDICTION_TERM_TARGET = 'jurisdiction_term';
export const TERM_KINDS = ['role', 'tier'];
/** Plain labels when a jurisdiction declares no role terms (ADR-0077 §6). */
export const CANONICAL_ROLE_LABEL = {
    provider: 'Providers',
    deployer: 'Deployers',
    importer: 'Importers',
    distributor: 'Distributors',
    authorized_representative: 'Authorized representatives',
};
/** Plain labels when a jurisdiction declares no tier terms (ADR-0077 §6). */
export const CANONICAL_TIER_LABEL = {
    unacceptable: 'Prohibited',
    high_risk: 'High risk',
    limited_risk: 'Transparency duties',
    minimal_risk: 'Minimal',
};
/** ADR-0077 §4: the intake question "Your role for this system", in plain words. */
export const ACTOR_ROLE_QUESTION_LABEL = {
    provider: 'We build it or sell it under our name',
    deployer: 'We use it in our work',
    importer: 'We bring it into the EU from outside',
    distributor: 'We resell it',
    authorized_representative: 'We represent a non-EU maker in the EU',
};
/**
 * ADR-0077 §4: the role answer is PREFILLED from the inventory lane and a
 * person confirms it. The engine never reads `lane`.
 */
export function prefillActorRoles(lane) {
    if (lane === 'buying')
        return ['deployer'];
    if (lane === 'building')
        return ['provider', 'deployer'];
    return [];
}
export class JurisdictionTermValidationError extends Error {
    errors;
    constructor(errors) {
        super(`Invalid jurisdiction term:\n  - ${errors.join('\n  - ')}`);
        this.name = 'JurisdictionTermValidationError';
        this.errors = errors;
    }
}
const TERM_KEY_RE = /^[a-z][a-z0-9_]{1,63}$/;
const JURISDICTION_RE = /^[A-Z]{2}(-[A-Za-z0-9]+)*$/;
const INTL_RE = /^INTL(-[A-Za-z0-9]+)+$/;
export function normalizeJurisdiction(j) {
    return j.trim().toUpperCase();
}
export function termTargetId(t) {
    return `${normalizeJurisdiction(t.jurisdiction)}:${t.kind}:${t.term_key}`;
}
/** Shape + frozen-set checks for one term (ADR-0077 §5). Every problem is listed. */
export function termShapeErrors(t) {
    const errors = [];
    const j = typeof t.jurisdiction === 'string' ? normalizeJurisdiction(t.jurisdiction) : '';
    if (!j || (!JURISDICTION_RE.test(j) && !INTL_RE.test(j))) {
        errors.push(`jurisdiction: expected a code such as EU, US-CO or US-NY-NYC`);
    }
    if (t.kind !== 'role' && t.kind !== 'tier')
        errors.push(`kind: must be role or tier`);
    if (typeof t.term_key !== 'string' || !TERM_KEY_RE.test(t.term_key)) {
        errors.push(`term_key: lower snake case, 2 to 64 characters (e.g. high_risk_ai_system)`);
    }
    if (typeof t.label !== 'string' || t.label.trim().length < 2 || t.label.length > 120) {
        errors.push(`label: required, 2 to 120 characters`);
    }
    if (t.kind === 'role' && !isCanonicalRole(t.canonical)) {
        errors.push(`canonical: a role term maps to one of ${CANONICAL_ROLES.join(', ')}`);
    }
    if (t.kind === 'tier' && !isRuleTier(t.canonical)) {
        errors.push(`canonical: a tier term maps to one of ${RULE_TIERS.join(', ')}`);
    }
    const hasRank = t.rank !== undefined && t.rank !== null;
    if (t.kind === 'role' && hasRank)
        errors.push(`rank: only tier terms have a rank`);
    if (t.kind === 'tier' && (!hasRank || !Number.isInteger(t.rank) || t.rank < 1 || t.rank > 20)) {
        errors.push(`rank: a tier term needs a whole-number rank from 1 to 20 (higher = stricter)`);
    }
    if (typeof t.citation !== 'string' || !t.citation.trim())
        errors.push(`citation: required (the section that defines the word)`);
    if (typeof t.source_url !== 'string' || !/^https?:\/\/\S+$/.test(t.source_url)) {
        errors.push(`source_url: a primary-source http(s) URL is required (ADR-0024 §8)`);
    }
    if (t.definition !== undefined && t.definition !== null && (typeof t.definition !== 'string' || t.definition.length > 4000)) {
        errors.push(`definition: at most 4,000 characters`);
    }
    return errors;
}
/**
 * ADR-0077 §2: a jurisdiction's tier mapping is monotonic. Ordered by rank
 * (higher = stricter), a higher rank never maps to a LOWER canonical tier,
 * and two active tier terms never share a rank.
 */
export function tierOrderErrors(terms) {
    const errors = [];
    const sorted = [...terms].filter((t) => t.rank !== null).sort((a, b) => a.rank - b.rank);
    for (let i = 1; i < sorted.length; i++) {
        const lo = sorted[i - 1];
        const hi = sorted[i];
        if (lo.rank === hi.rank) {
            errors.push(`rank: "${lo.term_key}" and "${hi.term_key}" share rank ${hi.rank}`);
            continue;
        }
        const a = TIER_RANK[lo.canonical];
        const b = TIER_RANK[hi.canonical];
        if (a !== undefined && b !== undefined && b < a) {
            errors.push(`rank: "${hi.term_key}" (rank ${hi.rank}) is stricter than "${lo.term_key}" (rank ${lo.rank}) ` +
                `but maps to a lower tier (${hi.canonical} below ${lo.canonical})`);
        }
    }
    return errors;
}
/**
 * The term to show for a canonical value: the jurisdiction's term whose
 * canonical value matches, if EXACTLY ONE active term does (ADR-0077 §3).
 */
export function termForCanonical(terms, kind, canonical) {
    const hits = terms.filter((t) => t.kind === kind && t.status === 'active' && t.canonical === canonical);
    return hits.length === 1 ? hits[0] : null;
}
function fromRow(r) {
    return {
        jurisdiction: r.jurisdiction,
        kind: r.kind,
        term_key: r.term_key,
        label: r.label,
        definition: r.definition,
        canonical: r.canonical,
        rank: r.rank === null || r.rank === undefined ? null : Number(r.rank),
        citation: r.citation,
        source_url: r.source_url,
        status: r.status,
        reviewer: r.reviewer,
        reviewed_at: r.reviewed_at,
    };
}
function parseState(raw) {
    if (!raw)
        return {};
    try {
        const v = JSON.parse(raw);
        return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    }
    catch {
        return {};
    }
}
/** Order: roles by label; tiers strictest first (rank descending). */
function compareTerms(a, b) {
    if (a.kind !== b.kind)
        return a.kind === 'role' ? -1 : 1;
    if (a.kind === 'tier')
        return (b.rank ?? 0) - (a.rank ?? 0);
    return a.label.localeCompare(b.label);
}
/**
 * A jurisdiction's terms. Active rows always; `include_pending` adds terms
 * filed but not yet applied (pending or approved `create` proposals) so a
 * form can offer a word it just proposed. Deprecated terms are not listed.
 */
export async function listJurisdictionTerms(db, jurisdiction, opts = {}) {
    const j = normalizeJurisdiction(jurisdiction);
    const rows = await db.query(`SELECT * FROM jurisdiction_terms WHERE jurisdiction = ? AND status = 'active'`, [j]);
    const out = rows.results.map(fromRow);
    if (opts.include_pending) {
        const have = new Set(out.map((t) => `${t.kind}:${t.term_key}`));
        const pend = await db.query(`SELECT change_id, proposed_state FROM proposed_changes
        WHERE target_type = ? AND change_type = 'create' AND status IN ('pending', 'approved')
          AND target_id LIKE ?
        ORDER BY created_at ASC, change_id ASC`, [JURISDICTION_TERM_TARGET, `${j}:%`]);
        for (const p of pend.results) {
            const s = parseState(p.proposed_state);
            if (normalizeJurisdiction(String(s.jurisdiction ?? '')) !== j)
                continue;
            if (termShapeErrors(s).length > 0)
                continue;
            const key = `${String(s.kind)}:${String(s.term_key)}`;
            if (have.has(key))
                continue;
            have.add(key);
            out.push({
                jurisdiction: j,
                kind: s.kind,
                term_key: String(s.term_key),
                label: String(s.label),
                definition: typeof s.definition === 'string' ? s.definition : null,
                canonical: String(s.canonical),
                rank: typeof s.rank === 'number' ? s.rank : null,
                citation: String(s.citation),
                source_url: String(s.source_url),
                status: 'pending',
                reviewer: null,
                reviewed_at: null,
                change_id: p.change_id,
            });
        }
    }
    return out.sort(compareTerms);
}
async function getStoredTerm(db, j, kind, key) {
    const r = await db.query(`SELECT * FROM jurisdiction_terms WHERE jurisdiction = ? AND kind = ? AND term_key = ?`, [j, kind, key]);
    return r.results[0] ? fromRow(r.results[0]) : null;
}
/**
 * File a pending `jurisdiction_term` proposal (`create` by default). Checks
 * the shape, the frozen canonical set, duplicates (an active term or a pending
 * proposal for the same key) and the tier order against the jurisdiction's
 * active tiers. Throws JurisdictionTermValidationError; nothing is written then.
 */
export async function proposeTerm(db, input, opts = {}) {
    const changeType = input.change_type ?? 'create';
    const t = {
        jurisdiction: normalizeJurisdiction(input.jurisdiction ?? ''),
        kind: input.kind,
        term_key: input.term_key,
        label: typeof input.label === 'string' ? input.label.trim() : input.label,
        definition: input.definition?.trim() || null,
        canonical: input.canonical,
        rank: input.rank ?? null,
        citation: typeof input.citation === 'string' ? input.citation.trim() : input.citation,
        source_url: typeof input.source_url === 'string' ? input.source_url.trim() : input.source_url,
        quote: input.quote?.trim() || null,
    };
    const errors = termShapeErrors(t);
    if (!input.proposer)
        errors.push('proposer: required — a term with no proposer has no provenance');
    if (errors.length)
        throw new JurisdictionTermValidationError(errors);
    const existing = await getStoredTerm(db, t.jurisdiction, t.kind, t.term_key);
    if (changeType === 'create') {
        if (existing) {
            throw new JurisdictionTermValidationError([
                existing.status === 'active'
                    ? `term_key: ${t.jurisdiction} already declares the ${t.kind} "${t.term_key}"`
                    : `term_key: "${t.term_key}" was deprecated; a retired key is not reused. Use a new key`,
            ]);
        }
        const dup = await db.query(`SELECT change_id FROM proposed_changes WHERE target_type = ? AND target_id = ? AND status IN ('pending', 'approved') LIMIT 1`, [JURISDICTION_TERM_TARGET, termTargetId(t)]);
        if (dup.results[0]) {
            throw new JurisdictionTermValidationError([`term_key: "${t.term_key}" is already waiting for review (${dup.results[0].change_id})`]);
        }
        if (t.kind === 'tier') {
            const active = (await listJurisdictionTerms(db, t.jurisdiction)).filter((x) => x.kind === 'tier');
            const order = tierOrderErrors([...active, { term_key: t.term_key, rank: t.rank ?? null, canonical: t.canonical }]);
            if (order.length)
                throw new JurisdictionTermValidationError(order);
        }
    }
    else if (!existing) {
        throw new JurisdictionTermValidationError([`term_key: ${t.jurisdiction} has no ${t.kind} "${t.term_key}" to ${changeType}`]);
    }
    else if (changeType === 'update' && (existing.canonical !== t.canonical || (existing.rank ?? null) !== (t.rank ?? null))) {
        throw new JurisdictionTermValidationError([
            `canonical / rank: a term's mapping is fixed once approved. Deprecate "${t.term_key}" and propose a new term instead`,
        ]);
    }
    const now = opts.now ?? new Date().toISOString();
    const changeId = `chg-${JURISDICTION_TERM_TARGET}-${Date.parse(now).toString(36)}-${randomId(6)}`;
    const state = { ...t, proposed_by: input.proposer };
    const rationale = input.rationale?.trim() ||
        `${t.jurisdiction} ${t.kind} "${t.label}" maps to ${t.canonical}${t.rank ? ` (rank ${t.rank})` : ''}. Cited ${t.citation}. ` +
            `Proposed by ${input.proposer}.`;
    await db.execute(`INSERT INTO proposed_changes (
       change_id, finding_id, target_type, target_id, change_type, current_state, proposed_state,
       ai_rationale, ai_confidence, source_kind, impact, severity, status, reviewer,
       raised_at, sla_deadline_at, reviewed_at, applied_at, created_at
     ) VALUES (?, NULL, ?, ?, ?, ?, ?, ?, 0, ?, '{}', 'routine', 'pending', NULL, ?, ?, NULL, NULL, ?)`, [
        changeId,
        JURISDICTION_TERM_TARGET,
        termTargetId(t),
        changeType,
        existing ? JSON.stringify(existing) : null,
        JSON.stringify(state),
        rationale,
        input.source_kind ?? 'admin_edit',
        now,
        computeSlaDeadline(now, 'routine'),
        now,
    ]);
    return {
        change_id: changeId,
        status: 'pending',
        term: {
            jurisdiction: t.jurisdiction,
            kind: t.kind,
            term_key: t.term_key,
            label: t.label,
            definition: t.definition ?? null,
            canonical: t.canonical,
            rank: t.rank ?? null,
            citation: t.citation,
            source_url: t.source_url,
            status: 'pending',
            reviewer: null,
            reviewed_at: null,
            change_id: changeId,
        },
    };
}
/**
 * Validate an APPROVED `jurisdiction_term` proposal and write it with the
 * reviewer and reviewed_at (ADR-0077 §5). `create` inserts; `update` changes
 * label / definition / citation / source URL only (the mapping is fixed);
 * `deprecate` retires the term. Returns the target id. Does not touch
 * `proposed_changes`; the caller stamps it.
 */
export async function applyApprovedTermState(db, change, opts = {}) {
    const s = parseState(change.proposed_state);
    const fromTarget = (change.target_id ?? '').split(':');
    const t = {
        ...s,
        jurisdiction: normalizeJurisdiction(String(s.jurisdiction ?? fromTarget[0] ?? '')),
        kind: (s.kind ?? fromTarget[1]),
        term_key: String(s.term_key ?? fromTarget[2] ?? ''),
    };
    const now = opts.now ?? new Date().toISOString();
    const reviewer = change.reviewer;
    const reviewedAt = change.reviewed_at ?? now;
    if (!reviewer)
        throw new JurisdictionTermValidationError(['reviewer: an applied term needs the reviewer who approved it']);
    const targetId = termTargetId(t);
    const existing = await getStoredTerm(db, t.jurisdiction, t.kind, t.term_key);
    if (change.change_type === 'deprecate') {
        if (!existing)
            throw new JurisdictionTermValidationError([`term ${targetId} does not exist`]);
        await db.execute(`UPDATE jurisdiction_terms SET status = 'deprecated', reviewer = ?, reviewed_at = ?, updated_at = ?
        WHERE jurisdiction = ? AND kind = ? AND term_key = ?`, [reviewer, reviewedAt, now, t.jurisdiction, t.kind, t.term_key]);
        return targetId;
    }
    const errors = termShapeErrors(t);
    if (errors.length)
        throw new JurisdictionTermValidationError(errors);
    if (change.change_type === 'update') {
        if (!existing)
            throw new JurisdictionTermValidationError([`term ${targetId} does not exist`]);
        if (existing.canonical !== t.canonical || (existing.rank ?? null) !== (t.rank ?? null)) {
            throw new JurisdictionTermValidationError([
                `canonical / rank: a term's mapping is fixed once approved. Deprecate "${t.term_key}" and propose a new term instead`,
            ]);
        }
        await db.execute(`UPDATE jurisdiction_terms SET label = ?, definition = ?, citation = ?, source_url = ?,
              reviewer = ?, reviewed_at = ?, source_kind = ?, updated_at = ?
        WHERE jurisdiction = ? AND kind = ? AND term_key = ?`, [t.label.trim(), t.definition ?? null, t.citation.trim(), t.source_url.trim(), reviewer, reviewedAt, change.source_kind, now,
            t.jurisdiction, t.kind, t.term_key]);
        return targetId;
    }
    if (existing) {
        throw new JurisdictionTermValidationError([
            existing.status === 'active'
                ? `term_key: ${t.jurisdiction} already declares the ${t.kind} "${t.term_key}"`
                : `term_key: "${t.term_key}" was deprecated; a retired key is not reused. Propose the term under a new key`,
        ]);
    }
    if (t.kind === 'tier') {
        const active = (await listJurisdictionTerms(db, t.jurisdiction)).filter((x) => x.kind === 'tier');
        const order = tierOrderErrors([...active, { term_key: t.term_key, rank: t.rank ?? null, canonical: t.canonical }]);
        if (order.length)
            throw new JurisdictionTermValidationError(order);
    }
    await db.execute(`INSERT INTO jurisdiction_terms (
       jurisdiction, kind, term_key, label, definition, canonical, rank, citation, source_url,
       status, reviewer, reviewed_at, source_kind, created_at, updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?, ?, ?, ?)`, [
        t.jurisdiction, t.kind, t.term_key, t.label.trim(), t.definition ?? null, t.canonical,
        t.kind === 'tier' ? t.rank : null, t.citation.trim(), t.source_url.trim(),
        reviewer, reviewedAt, change.source_kind, now, now,
    ]);
    return targetId;
}
// ============================================================
// Rule ↔ term validation (ADR-0077 §5)
// ============================================================
/**
 * Check every `role_terms` / `tier_term` key on a rule's obligations against
 * the rule's EXACT jurisdiction. `propose`: a pending term is allowed (so the
 * form can file a term and a rule together). `apply`: the term must be active.
 * When term fields are present the canonical fields must equal their mapping.
 * Errors name the jurisdiction and list its declared terms. Returns [] when
 * valid; obligations with no term fields are never checked here.
 */
export async function validateRuleTerms(db, jurisdiction, obligations, phase) {
    const needs = obligations.some((o) => (Array.isArray(o.role_terms) && o.role_terms.length > 0) || typeof o.tier_term === 'string');
    if (!needs)
        return [];
    const j = normalizeJurisdiction(jurisdiction);
    const terms = await listJurisdictionTerms(db, j, { include_pending: true });
    const byKey = (kind, key) => terms.find((t) => t.kind === kind && t.term_key === key);
    const declared = (kind) => {
        const list = terms.filter((t) => t.kind === kind && (phase === 'propose' || t.status === 'active')).map((t) => t.term_key);
        return list.length ? `${j} declares ${kind} terms: ${list.join(', ')}` : `${j} declares no ${kind} terms yet. Propose one first`;
    };
    const errors = [];
    obligations.forEach((o, i) => {
        const roleTerms = Array.isArray(o.role_terms) ? o.role_terms : [];
        if (roleTerms.length > 0) {
            const canon = new Set();
            let complete = true;
            for (const key of roleTerms) {
                const t = byKey('role', key);
                if (!t) {
                    errors.push(`obligations[${i}].role_terms: "${key}" is not a ${j} role term. ${declared('role')}`);
                    complete = false;
                }
                else if (phase === 'apply' && t.status !== 'active') {
                    errors.push(`obligations[${i}].role_terms: "${key}" is still waiting for review. Approve the term before this rule`);
                    complete = false;
                }
                else {
                    canon.add(t.canonical);
                }
            }
            const scope = new Set(o.scope ?? []);
            if (complete && (scope.size !== canon.size || [...canon].some((c) => !scope.has(c)))) {
                errors.push(`obligations[${i}].scope: must be [${[...canon].sort().join(', ')}], the roles its terms map to (got [${[...scope].sort().join(', ')}])`);
            }
        }
        if (typeof o.tier_term === 'string') {
            const t = byKey('tier', o.tier_term);
            if (!t) {
                errors.push(`obligations[${i}].tier_term: "${o.tier_term}" is not a ${j} tier term. ${declared('tier')}`);
            }
            else if (phase === 'apply' && t.status !== 'active') {
                errors.push(`obligations[${i}].tier_term: "${o.tier_term}" is still waiting for review. Approve the term before this rule`);
            }
            else if (o.tier !== t.canonical) {
                errors.push(`obligations[${i}].tier: must be ${t.canonical}, the tier "${o.tier_term}" maps to (got ${o.tier ?? 'none'})`);
            }
        }
    });
    return errors;
}
function randomId(len) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    for (let i = 0; i < len; i++)
        out += chars[Math.floor(Math.random() * chars.length)];
    return out;
}
//# sourceMappingURL=jurisdiction-term-service.js.map